import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import { parseHHMM } from './timelineGeometry';

/**
 * InspectorBar — floating clip editor anchored above the selected clip.
 *
 * Handles the common case that a schedule is not a single-day affair: users
 * schedule "Next 30 days, 9 AM–6 PM, weekdays" and the UI has to reflect that
 * single intent without implying 30 separate rows.
 *
 * Fields, top to bottom:
 *   Header       — live summary: "Apr 23 → May 22 · 30 days · Weekdays only"
 *                  (or "Today only" when the range collapses to one day).
 *   Time row     — Start / End HH:MM.
 *   Date row     — Start date / End date (native date pickers).
 *   Recurrence   — chip group: Every day / Weekdays / Weekends / Custom.
 *                  Custom reveals a 7-toggle row (S M T W T F S).
 *   Delete       — two-stage: first click reveals a confirm banner that
 *                  spells out the whole span, second click commits. The
 *                  user asked explicitly for this warning ("give a warning
 *                  on deletion of how long the content").
 *
 * Positioning: fixed, anchored via data-clip-id on ClipBlock. Flips below
 * when there isn't enough room above. Repositions on scroll/resize.
 *
 * Dismiss: click outside, ESC, or parent clearing selectedClipId.
 */

function toISODate(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function daysBetween(startISO, endISO) {
  if (!startISO || !endISO) return 1;
  const a = new Date(`${startISO}T00:00:00`);
  const b = new Date(`${endISO}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 1;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

function formatDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function InspectorBar({ intent, onPatch, onDelete, onClose }) {
  const initialStartDate = toISODate(intent?.startDate) || toISODate(new Date());
  const initialEndDate = toISODate(intent?.endDate) || initialStartDate;

  const [draft, setDraft] = useState({
    startTime: intent?.startTime || '',
    endTime: intent?.endTime || '',
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pos, setPos] = useState(null);
  const rootRef = useRef(null);


  useEffect(() => {
    setDraft({
      startTime: intent?.startTime || '',
      endTime: intent?.endTime || '',
      startDate: toISODate(intent?.startDate) || toISODate(new Date()),
      endDate: toISODate(intent?.endDate) || toISODate(intent?.startDate) || toISODate(new Date()),
    });
    setConfirmDelete(false);
  }, [intent?.id, intent?.startTime, intent?.endTime, intent?.startDate, intent?.endDate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      onClose?.();
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('touchstart', onPointerDown);
    };
  }, [onClose]);

  // Anchor against the selected clip. Slightly oversized hit box so the tip
  // still points at the clip after layout nudges.
  useLayoutEffect(() => {
    if (!intent?.id) return undefined;
    const compute = () => {
      const target = document.querySelector(`[data-clip-id="${CSS.escape(intent.id)}"]`);
      if (!target) { setPos(null); return; }
      const rect = target.getBoundingClientRect();
      const floaterW = 320;
      const floaterH = confirmDelete ? 260 : 220;
      const margin = 8;
      const centerX = rect.left + rect.width / 2;
      let left = centerX - floaterW / 2;
      left = Math.max(margin, Math.min(window.innerWidth - floaterW - margin, left));
      const spaceAbove = rect.top;
      const placeBelow = spaceAbove < floaterH + margin;
      const top = placeBelow
        ? Math.min(window.innerHeight - floaterH - margin, rect.bottom + margin)
        : Math.max(margin, rect.top - floaterH - margin);
      setPos({ top, left, placeBelow });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [intent?.id, intent?.startTime, intent?.endTime, intent?.startDate, intent?.endDate, confirmDelete]);

  const summary = useMemo(() => {
    const days = daysBetween(draft.startDate, draft.endDate);
    const sameDay = draft.startDate === draft.endDate;
    const rangeLabel = sameDay
      ? formatDateLabel(draft.startDate)
      : `${formatDateLabel(draft.startDate)} → ${formatDateLabel(draft.endDate)}`;
    const dayCount = sameDay ? '1 day' : `${days} days`;
    return { rangeLabel, dayCount, days };
  }, [draft.startDate, draft.endDate]);

  if (!intent) return null;

  const commitTime = (field) => {
    const raw = draft[field];
    const parsed = parseHHMM(raw);
    if (parsed === null) {
      setDraft((d) => ({ ...d, [field]: intent[field] }));
      return;
    }
    if (field === 'startTime' && parsed >= intent.endMinutes) {
      setDraft((d) => ({ ...d, startTime: intent.startTime }));
      return;
    }
    if (field === 'endTime' && parsed <= intent.startMinutes) {
      setDraft((d) => ({ ...d, endTime: intent.endTime }));
      return;
    }
    if (raw === intent[field]) return;
    onPatch(intent.id, { [field]: raw });
  };

  // Commit a date the moment it changes. A native <input type="date"> only
  // emits complete, valid ISO values through onChange, so there is nothing to
  // wait for on blur — and relying on blur lost the edit entirely: picking a
  // date then clicking away fires the outside-click handler (onClose), which
  // unmounts this popover before React ever dispatches the input's blur.
  const applyDate = (field, raw) => {
    if (!raw) {
      // Empty (user cleared the field) — reflect it but don't patch a blank.
      setDraft((d) => ({ ...d, [field]: raw }));
      return;
    }
    // Clamp start <= end: nudge the other end if the pair would invert.
    let nextStart = field === 'startDate' ? raw : draft.startDate;
    let nextEnd = field === 'endDate' ? raw : draft.endDate;
    if (field === 'startDate' && nextEnd && nextEnd < raw) nextEnd = raw;
    if (field === 'endDate' && nextStart && nextStart > raw) nextStart = raw;
    setDraft((d) => ({ ...d, startDate: nextStart, endDate: nextEnd }));
    const patch = {};
    if (nextStart !== toISODate(intent.startDate)) patch.startDate = nextStart;
    if (nextEnd !== toISODate(intent.endDate)) patch.endDate = nextEnd;
    if (Object.keys(patch).length) onPatch(intent.id, patch);
  };


  const confirmAndDelete = () => {
    onDelete?.(intent.id);
  };

  const style = pos
    ? { top: `${pos.top}px`, left: `${pos.left}px` }
    : { top: '-9999px', left: '-9999px' };

  return (
    <div
      ref={rootRef}
      className={`inspector-floater${pos?.placeBelow ? ' inspector-floater--below' : ''}`}
      style={style}
      role="dialog"
      aria-label="Clip inspector"
    >
      <div className="inspector-floater__summary">
        <div className="inspector-floater__summary-range">{summary.rangeLabel}</div>
        <div className="inspector-floater__summary-meta">
          {summary.dayCount} · {intent.startTime}–{intent.endTime}
        </div>
      </div>

      <div className="inspector-floater__row">
        <label className="inspector-floater__field">
          <span>Start time</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={draft.startTime}
            onChange={(e) => setDraft((d) => ({ ...d, startTime: e.target.value }))}
            onBlur={() => commitTime('startTime')}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
        </label>
        <label className="inspector-floater__field">
          <span>End time</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={draft.endTime}
            onChange={(e) => setDraft((d) => ({ ...d, endTime: e.target.value }))}
            onBlur={() => commitTime('endTime')}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
        </label>
      </div>

      <div className="inspector-floater__row">
        <label className="inspector-floater__field">
          <span>Start date</span>
          <input
            type="date"
            value={draft.startDate}
            onChange={(e) => applyDate('startDate', e.target.value)}
          />
        </label>
        <label className="inspector-floater__field">
          <span>End date</span>
          <input
            type="date"
            value={draft.endDate}
            min={draft.startDate}
            onChange={(e) => applyDate('endDate', e.target.value)}
          />
        </label>
      </div>

      {confirmDelete ? (
        <div className="inspector-floater__confirm">
          <div className="inspector-floater__confirm-title">
            <AlertTriangle size={14} /> Delete this schedule?
          </div>
          <div className="inspector-floater__confirm-body">
            Cancels the entire run — <strong>{summary.rangeLabel}</strong>
            {summary.days > 1 ? ` (${summary.dayCount})` : ''},
            {' '}{intent.startTime}–{intent.endTime}. This cannot be undone.
          </div>
          <div className="inspector-floater__confirm-actions">
            <button
              type="button"
              className="inspector-floater__confirm-cancel"
              onClick={() => setConfirmDelete(false)}
            >
              Keep it
            </button>
            <button
              type="button"
              className="inspector-floater__confirm-go"
              onClick={confirmAndDelete}
            >
              Yes, delete
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="inspector-floater__delete"
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 size={14} /> Delete schedule
        </button>
      )}
    </div>
  );
}
